import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchObjets } from '../store/objet';
import { urlFor } from '../configs/sanityClient';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const constantImageUrl = 'https://static.vecteezy.com/system/resources/thumbnails/035/147/887/small/ai-generated-antique-armchair-throne-on-transparent-background-image-png.png';

const getImageUrl = (image) => {
  if (!image) return constantImageUrl;
  
  try {
    const assetRef = image.file?.asset?._ref;
    if (assetRef) {
      return urlFor(image.file).url();
    }
    return image.path || constantImageUrl;
  } catch (error) {
    console.error('Error building Sanity image URL:', error);
    return constantImageUrl;
  }
};

const NewArrivals = () => {
  const dispatch = useDispatch();
  const objets = useSelector((state) => state.objet.items);
  const limitedObjets = objets ? objets.slice(0, 5) : [];
  const [activeStep, setActiveStep] = React.useState({});

  useEffect(() => {
    dispatch(fetchObjets());
  }, [dispatch]);

  const handleNext = (objetId) => {
    setActiveStep((prev) => ({
      ...prev,
      [objetId]: (prev[objetId] || 0) + 1
    }));
  };

  const handleBack = (objetId) => {
    setActiveStep((prev) => ({
      ...prev,
      [objetId]: Math.max((prev[objetId] || 0) - 1, 0)
    }));
  };

  const renderImages = (objet) => {
    if (!objet || !objet.images || !Array.isArray(objet.images) || objet.images.length === 0) {
      return (
        <Box key={`${objet?.dbId || 'default'}-default`}>
          <img
            src={constantImageUrl}
            alt={`${objet?.nom || 'Unknown'} - Default Image`}
            style={styles.cardImage}
          />
        </Box>
      );
    }

    const filteredImages = objet.images.filter(image => image !== null && image !== undefined);
    const currentStep = activeStep[objet.dbId] || 0;
    const maxSteps = filteredImages.length;

    return (
      <Box sx={{ position: 'relative' }}>
        <Box>
          <img
            src={getImageUrl(filteredImages[currentStep])}
            alt={`${objet.nom} - Image ${currentStep + 1}`}
            style={styles.cardImage}
            onError={(e) => {
              e.target.src = constantImageUrl;
              e.target.alt = `${objet.nom} - Fallback Image`;
            }}
          />
        </Box>
        {maxSteps > 1 && (
          <>
            <IconButton
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
              onClick={() => handleBack(objet.dbId)}
              disabled={currentStep === 0}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
              onClick={() => handleNext(objet.dbId)}
              disabled={currentStep === maxSteps - 1}
            >
              <ArrowForwardIosIcon />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 0.5,
              }}
            >
              {filteredImages.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: currentStep === index ? '#A68E5D' : 'rgba(255, 255, 255, 0.5)',
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <div style={styles.newArrivals}>
      <h2 style={styles.title}>New Arrivals</h2>
      <a href="/tousLesObjets" style={styles.discoverMore}>Discover More</a>
      <div style={styles.cardContainer}>
        {limitedObjets && limitedObjets.length > 0 ? (
          limitedObjets.map((objet) => (
            <div key={objet?.dbId || `objet-${Math.random()}`} style={styles.card}>
              <div style={styles.cardDetails}>
                <h3 style={styles.cardTitle}>{objet?.nom || 'Untitled'}</h3>
                {renderImages(objet)}
                <div style={styles.infoContainer}>
                  <span style={styles.pays}>{objet?.pays?.nom || "Unknown Country"}</span>
                  <span style={styles.price}>{objet?.prix || "N/A"}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p style={styles.noArrivals}>No new arrivals available</p>
        )}
      </div>
    </div>
  );
};

const styles = {
  newArrivals: {
    textAlign: 'center',
    backgroundColor: '#232323',
    padding: '20px',
    color: '#e0e0e0',
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '10px',
    color: '#A68E5D',
    fontFamily: 'Georgia',
  },
  discoverMore: {
    fontSize: '1rem',
    color: '#ccc',
    textDecoration: 'none',
    marginBottom: '20px',
    display: 'inline-block',
  },
  cardContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '27px',
    flexWrap: 'wrap',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    width: '200px',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
    textAlign: 'left',
    color: '#333',
    padding: '10px',
  },
  cardImage: {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    display: 'block',
    borderRadius: "10px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)"
  },
  cardDetails: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '10px',
  },
  cardTitle: {
    fontSize: '1rem',
    marginTop: '-15px',
    fontFamily: "Poppins, sans-serif",
    fontWeight: 100,
  },
  infoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
  },
  price: {
    fontWeight: 'bold',
    fontSize: '1rem',
    color: '#b5a642',
    fontFamily: "Poppins, sans-serif",
    fontWeight: 100
  },
  pays: {
    fontSize: '1rem',
    color: '#333',
    fontFamily: "Poppins, sans-serif",
    fontWeight: 100
  },
  noArrivals: {
    color: '#ccc',
    fontSize: '1rem',
    fontFamily: "Poppins, sans-serif",
    fontWeight: 100
  },
};

export default NewArrivals;
